import { Add, Delete, Edit } from "@mui/icons-material";
import BlindsClosedIcon from "@mui/icons-material/BlindsClosed";
import {
  Grid,
  IconButton,
  Skeleton,
  Stack,
  TableCell,
  Tooltip,
  Typography,
} from "@mui/material";
import EmptyContent from "components/EmptyContent";
import List from "components/UI/List";
import { useAlert } from "contexts/alertContext";

import { Feature } from "models/api";
import React, {  useCallback, useEffect, useState } from "react";
import { QueryClient, useMutation } from "react-query";
import Search from "components/Search/Search";
import { handleError } from "helpers/utils";
import useFetchSection from "hooks/useFetchSection";
import DeleteModal from "components/UI/Form/partials/Delete";
import AddModal from "components/UI/Form/partials/Add";
import { InputTypes } from "constants/enums";
import { IFormField } from "models/app";
import EditModal from "components/UI/Form/partials/Edit";
import { IService ,IServiceInput} from "models/services";
import { addService, deleteService, editService, getServices, ServicesCells } from "framework/service";
import { getPlans } from "framework/plans";
import { IPlan } from "models/plan";

interface Props {
  feature: Feature;
}

const Services: React.FC<Props> = ({ feature }) => {
  const [loading2, setLoading2] = useState<boolean>(false);
  const [FilterServices, setFilterServices] = useState<IService[]>([] as IService[]);
  const [plans, setPlans] = useState<IPlan[]>([] as IPlan[]);
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [openEdit, setOpenEdit] = useState<boolean>(false);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [selectedService, setSelectedService] = useState<IService|null>(null);
  const [inputObj, setInputObj] = useState<IServiceInput>({} as IServiceInput);
  const { setAlert } = useAlert();

  // eslint-disable-next-line
  const queryClient = new QueryClient();

  const addServiceMutation = useMutation({
    mutationFn: (createInput: IServiceInput) => {
      return addService(createInput);
    },
  });

  const editServiceMutation = useMutation({
    mutationFn: (data: { createInput: IServiceInput; planID: string }) => {
      return editService(data.createInput, data.planID);
    },
  });

  const deleteCitiesMutation = useMutation({
    mutationFn: (id: string) => {
      return deleteService(id);
    },
  });


  const fetchPlans = useCallback(async () => {
    try {
      let response: any = await queryClient.fetchQuery("get plans", () =>
        getPlans()
      );
      setPlans(response);
    
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
    }

    // eslint-disable-next-line
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchPlans();
  }, []);

  const {
    loading,
    data: Services,
    error,
    revalidate,
  } = useFetchSection({
    fetchData: () => getServices(),
    fetchPermission: async () => null,
    section: "Services",
    onSuccess: (data) => setFilterServices(data),
  
  });

  if (loading)
    return (
      <Stack spacing={2} p={2}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Skeleton variant="rectangular" key={i} width="100%" height={30} />
        ))}
      </Stack>
    );

  const handleChange = (name: string, value: any) => {
    if(name === 'Plans'){
      const array:{label:string,value:string}[] = value
      const data = array.map(item => +item.value)
      setInputObj((prev) => ({
        ...prev,
        [name]: data,
      }));

      return
    }
    setInputObj((prev) => ({
      ...prev,
      [name]: value || '',
    }));
  };

  const handleOpenAdd = () => setOpenAdd(true);


  const handleCloseAdd = () => {
    setOpenAdd(false);
    setInputObj({} as IServiceInput);
  };

  const handleOpenEdit = (Service: IService) => {
    setSelectedService(Service);
    setOpenEdit(true);
  };
  const handleCloseEdit = () => {
    setOpenEdit(false);
    setSelectedService(null);
    setInputObj({} as IServiceInput);
  };

  const handleOpenDelete = (Service: IService) => {
    setSelectedService(Service);
    setOpenDelete(true);
  };

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setSelectedService(null);
  };


  const getBodyCells = (row: IService) => {
    let cells = Object.keys(row).map((key: string, i: number) => {
      if (key === "serviceName")
        return {
          id: "serviceName",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row?.serviceName}</Typography>
            </TableCell>
          ),
        };
      if (key === "planNames")
        return {
          id: "planNames",
          element: (
            <TableCell align={"left"} key={i}>
              <Typography variant="body2">{row.planNames}</Typography>
            </TableCell>
          ),
        };

  
  

      if (key === "serviceId")
        return {
          id: "actions",
          element: (
            <TableCell align={"left"} key={i}>
              <Stack
                direction={"row"}
                alignContent={"center"}
                spacing={1}
              >
                
                  <Tooltip title={"Edit"}>
                    <IconButton
                      size={"small"}
                      color={"primary"}
                      onClick={() => handleOpenEdit(row)}
                    >
                      <Edit />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title={"Delete"}>
                    <IconButton
                      size={"small"}
                      sx={{
                        color: "red",
                      }}
                      onClick={() => handleOpenDelete(row)}
                    >
                      <Delete />
                    </IconButton>
                  </Tooltip>
          
            
              </Stack>
            </TableCell>
          ),
        };
    });
    return cells;
  };

  const handleAdd = async () => {

  

    if (!inputObj.serviceName )
      return setAlert({
        open: true,
        message: "Service name is required",
        type: "error",
      });

      if (!inputObj?.Plans?.length )
        return setAlert({
          open: true,
          message: "At least 1 plan is required",
          type: "error",
        });


    try {
      setLoading2(true);

      await addServiceMutation.mutateAsync(inputObj);
      setAlert({
        open: true,
        message: "Service added successfully",
        type: "success",
      });
      revalidate();
      handleCloseAdd();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleEdit = async () => {
    
    if (inputObj.serviceName === '')
      return setAlert({
        open: true,
        message: "Service Name Required",
        type: "error",
      });

      if (inputObj.Plans && inputObj.Plans.length === 0)
        return setAlert({
          open: true,
          message: "At least 1 plan is required",
          type: "error",
        });

    
        if (!inputObj.serviceName && !inputObj.Plans )
          return setAlert({
            open: true,
            message: "No Change Happened",
            type: "error",
          });


    try {
      setLoading2(true);
  
      await editServiceMutation.mutateAsync({
        createInput:inputObj,
        planID: selectedService!.serviceId.toString(),
      });
      setAlert({
        open: true,
        message: "Service updated successfully",
        type: "success",
      });
      revalidate();
      handleCloseEdit();
      
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading2(true);
      await deleteCitiesMutation.mutateAsync(selectedService!.serviceId.toString());
      setAlert({
        open: true,
        message: "Service deleted successfully",
        type: "success",
      });
      revalidate();
      handleCloseDelete();
      setLoading2(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading2(false);
    }
  };

  const handleSearch = async (value: string) => {
    if (!value) return setFilterServices(Services!);
    let filtered = Services!.filter((service) =>
      service.serviceName.toLowerCase().includes(value.toLowerCase()) || service.plans.map(planId => plans.find(plan => plan.planId === planId)?.planName).join(', ').toString().toLowerCase().includes(value)
    );
    setFilterServices(filtered);
  };



  let plansOptions = plans.map((plan) =>
    {
      return { value: plan.planId.toString(), label: plan.planName };
    });

  let addBasicFields: IFormField[] = [
    {
      name: 'serviceName',
      label: 'Service Name',
      type: InputTypes.TEXT,
      placeholder: 'Enter Service Name',
      internalLabel: true,
      required: true
    },
    {
      name: 'Plans',
      label: 'Plans',
      placeholder: 'Select Plans',
      type: InputTypes.MULTI_SELECT,
      options:plansOptions,
      internalLabel: true,
      multiple: true,
      required: true
    }, 
    
  ];


  let editBasicFields: IFormField[] = [
    {
      name: 'serviceName',
      label: 'Service Name',
      type: InputTypes.TEXT,
      placeholder: 'Enter Service Name',
      defaultValue:selectedService ? selectedService.serviceName : '',
      internalLabel: true,
      required: true
    },
    {
      name: 'Plans',
      label: 'Plans',
      placeholder: 'Select Plans',
      type: InputTypes.MULTI_SELECT,
      options:plansOptions,
      multiple:true,
      defaultValue: selectedService ? selectedService.plans.map((planId) =>
        {
          return { value: planId.toString(), label: plans.find(plan => plan.planId === planId )?.planName };
        }):[],
      internalLabel: true,
      required: true
    }, 
  

  ];

  return (
    <>
      <Stack spacing={2} p={2}>
        <Grid
          container
          direction={"row"}
          justifyContent={"space-between"}
          alignItems="center"
          sx={{ mb: 4 }}
        >
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Typography
              variant="h3"
              color={ "header.primary"}
            >
              {(feature.name)}
            </Typography>
          
              <Tooltip title={"Add"}>
                <IconButton
                  sx={{
                    color: "red",
                    height: 40,
                    width: 40,
                    transition: "0.5s",
                  }}
                  onClick={handleOpenAdd}
                >
                  <Add />
                </IconButton>
              </Tooltip>
      
          </Stack>
          <Stack
            direction={"row"}
            spacing={1}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Stack
              direction={{ lg: "row", xs: "column" }}
              spacing={1}
              width={"100%"}
            >
              <Search handleChange={handleSearch} placeholder={"Search"} />
           
            </Stack>
          </Stack>
        </Grid>

        { FilterServices.length > 0 && (
          <List
            headCells={ServicesCells}
            data={FilterServices}
            getBodyCells={getBodyCells}
            feature={feature}
            canEdit={false}
          />
        )}
        {(!FilterServices || FilterServices.length === 0) &&
           
            <EmptyContent
              icon={<BlindsClosedIcon sx={{ fontSize: "3.5rem " }} />}
              header={"No Services"}
              message={"No Services Exists Yet"}
            />
          }

      
      </Stack>

      {openAdd && (
        <AddModal
          open={openAdd}
          loading={loading2}
          title="Add Service"
          disabled={!inputObj?.Plans?.length || !inputObj.serviceName }
          basicFields={addBasicFields}
          onClose={handleCloseAdd}
          onSubmit={handleAdd}
          onChanges={handleChange}
        />
      )}
      {openEdit && (
        <EditModal
          open={openEdit}
          loading={loading2}
          title="Edit Service"
          disabled={!inputObj?.Plans && !inputObj.serviceName }
          basicFields={editBasicFields}
          onClose={handleCloseEdit}
          onSubmit={handleEdit}
          onChanges={handleChange}
        />
      )}

{openDelete && (
        <DeleteModal
          open={openDelete}
          loading={loading2}
          title="Delete Service"
          description="Are You Sure You Want to delete this Service"
          onClose={handleCloseDelete}
          onSubmit={handleDelete}
        />
      )}

    </>
  );
};

export default Services;
